import { isEmpty } from 'lodash';
import { AskScope } from '../api';
import { withDictationTag } from './dictationTag';
import { languageDirective } from './languageDirective';
import { noCardLinkDirective } from './noCardLinkDirective';

export interface AskPromptDecoration {
  /**
   * The text this turn's dictation produced, if any. Marked on the wire so the model knows which
   * words were heard rather than typed — see `withDictationTag`.
   */
  dictatedText?: string;
}

/**
 * Directives carry what the client knows and the agent otherwise cannot. The gateway holds
 * the system prompt and receives nothing from us but the prompt text and the scope, so the
 * user's UI language, the fact that a turn was dictated, and how this client renders an
 * answer have to ride along in the prompt itself.
 *
 * Anything the gateway's system prompt already says has been taken back out — the agent's
 * base instructions, its response conventions, its groundedness rules, and its card-linking
 * rules are all the backend's, and eventually the rest of this will be too. What stays is only
 * what a server-side prompt has no way to know.
 *
 * They are split by surface rather than shared, because the two surfaces want different
 * answers and the prompt budget is shared with the user's own question (see
 * `askPromptLimit`) — an instruction that cannot apply is not free.
 *
 * Only the raw prompt is ever displayed; directives are never shown.
 */

/**
 * Directives for the Ask panel: the user's UI language, and the dictation mark when the words
 * were spoken.
 *
 * Nothing about card links. The gateway's system prompt already tells the agent to link every card
 * it names and where to get the URL, so the panel says nothing on the subject at all.
 *
 * `_scope` is unused now that card links come from the `cardLinkUrl` the tools return rather
 * than from a path built around the scope's channel slug. It stays in the signature because
 * it is the shape `CoastAskAgent`'s `decoratePrompt` hook passes.
 */
export function withChatDirectives(
  prompt: string,
  _scope: AskScope,
  decoration?: AskPromptDecoration,
): string {
  // The tag goes on the question, inside the join, so the directives stay outside it — they are
  // instructions to the model, not words anybody dictated.
  const question = withDictationTag(prompt, decoration?.dictatedText);
  return [question, languageDirective()].join('\n\n');
}

/**
 * Directives for a Capture: the language, and the dictation mark when the words were spoken.
 *
 * Deliberately **no instruction to create a card**. The request's `action` is what tells the
 * gateway a Capture is a card being built, and with it set the gateway owns the instruction
 * wording outright — saying it again here would be a second, weaker copy of something the body
 * already states, paid for out of the same budget as the user's own words.
 *
 * Nothing about links either: nothing renders this answer. It is parsed into field data and the
 * form takes over, so a markdown link in it would be text nobody ever sees.
 *
 * Nothing about the form's current values, which are a body field now (`baseline`) rather than a
 * paragraph of prompt — see `captureBaseline`. They were never an instruction: they are the material
 * the turn is about, and the gateway has somewhere to put material.
 *
 * **A Capture with no words returns nothing at all**, so `buildAgUiRequestBody` leaves `prompt` off
 * the body entirely. That is a photo on its own, whether it is the first Capture or a fifth: there is
 * no question, and the language directive alone would send a turn whose whole content was an
 * instruction about how to phrase a reply that nothing displays. It would also read as the user's
 * question, because that is the field it arrives in.
 */
export function withCaptureDirectives(
  prompt: string,
  _scope: AskScope,
  decoration?: AskPromptDecoration,
): string {
  // Checked before the tag rather than after: a dictated span that no longer matches makes
  // `withDictationTag` wrap the whole prompt, which would turn '' into an empty pair of tags.
  if (isEmpty(prompt.trim())) {
    return '';
  }
  return [withDictationTag(prompt, decoration?.dictatedText), languageDirective()].join('\n\n');
}

/**
 * Directives for the summary widgets: the language, plus one line ruling out links.
 *
 * A summary is about the one record the user already has open, so the only link it could
 * offer points back at that record, and `AiSummary` renders its markdown without the
 * transcript's link handler — a link it did produce would leave the app on a full page load
 * rather than routing. Saying nothing is not the same as saying no, and here it is not even
 * neutral: the gateway's system prompt asks for a markdown link on every card the agent names.
 * `noCardLinkDirective` overrides it explicitly.
 */
export function withSummaryDirectives(prompt: string): string {
  return [prompt, noCardLinkDirective(), languageDirective()].join('\n\n');
}
